import { useState } from 'react';
import { BsPlusLg, BsTrash3, BsPen } from 'react-icons/bs';

import { Pagination, TableColumn } from 'types/table';
import { Org } from 'api/orgs/types';

import { TableActions } from 'components/ui/TableActions';
import { Checkbox } from 'components/ui/Checkbox';
import Table from 'components/ui/Table';
import { checkboxProps } from 'utils';
import { useAuthStore } from 'store/auth';
import { maskValue } from 'utils/mask';
interface Props {
  isLoading: boolean;
  data: Org[];
  onCreate: (id: string) => void;
  onDelete: (id: string[]) => void;
  onUpdate: (org: Org) => void;
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  pagination?: Pagination;
}

export const OrgTable = ({
  isLoading,
  onCreate,
  onUpdate,
  onDelete,
  data,
  selected,
  setSelected,
  pagination
}: Props) => {
  const [activeMenu, setActiveMenu] = useState('');

  const { user } = useAuthStore();
  const deepMask = (value: any): any => {
    if (value === null || value === undefined) return value;
    if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) return maskValue(value);
    if (Array.isArray(value)) return value.map(deepMask);
    if (typeof value === 'object') {
      const maskedObj: Record<string, any> = {};
      for (const key in value) maskedObj[key] = deepMask(value[key]);
      return maskedObj;
    }
    return value;
  };

  const maskedData = user?.isCensorshipEnabled
    ? data.map((org) => {
      const masked = deepMask(org);
      return { ...masked, _id: org._id };
    })
    : data;

  const columns: TableColumn[] = [
    {
      key: 'name',
      name: 'Organization name',
      sort: ['name'],
      cell: (el: Org, i) => (
        <div className="flex items-center gap-3">
          {user?.userRole !== 'moderator' ? <Checkbox {...checkboxProps(el._id, selected, setSelected)} /> : null}
          <span className="badge primary !p-3">0{(i + 1).toLocaleString()}</span>
          <span>{el.name}</span>
        </div>
      )
    },
    {
      key: 'type',
      name: 'Type',
      sort: ['type'],
      cell: (el: Org) => el.type.name
    },
    {
      key: 'students',
      name: 'Students',
      align: 'center',
      cell: (el: Org) =>
        user?.isCensorshipEnabled
          ? maskValue(el.students.length)
          : el.students.length
    },
    {
      key: 'isActive',
      name: 'Status',
      align: 'center',
      cell: (el: Org) =>
        user?.isCensorshipEnabled ? (
          maskValue(el.isActive ? 'Active' : 'Inactive')
        ) : (
          <div className={`badge ${el.isActive ? 'success' : 'danger'}`}>
            {el.isActive ? 'Active' : 'Inactive'}
          </div>
        )
    },
    {
      key: 'actions',
      name: '',
      align: 'center',
      cell: (el: Org) => (
        <TableActions activeMenu={activeMenu} setActiveMenu={setActiveMenu} itemID={el._id}>
          {(user?.userRole === 'admin' || user?.userRole === 'moderator') && (
            <>
              <li onClick={() => onCreate(el._id)}>
                <BsPlusLg className="text-success-100" />
                Add Organization Admin
              </li>
              <li onClick={() => onUpdate(el)}>
                <BsPen className="text-light-200" />
                Edit Organization
              </li>
              <li onClick={() => onDelete([el._id])}>
                <BsTrash3 className="text-danger" />
                Delete Organization
              </li>
            </>
          )}
        </TableActions>
      )
    }
  ];

  return <Table isLoading={isLoading} data={maskedData} columns={columns} pagination={pagination} className="orgTable" />;
};
